import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import {
  Users, Copy, Share2, TrendingUp, Wallet, Gift, Crown,
  ArrowLeft, Loader2, Send, Link2, UserPlus, Percent, ArrowDownToLine
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import useStore from "@/store/useStore";
import { API_URL } from "@/config/api";

const fmt = (n) => Number(n || 0).toLocaleString("ru-RU", { maximumFractionDigits: 2 });

export default function ReferralsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token, fetchUser } = useStore();
  const locale = i18n.language === "ru" ? "ru-RU" : i18n.language === "zh" ? "zh-CN" : "en-US";

  const [stats, setStats] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [s, e, u] = await Promise.all([
        fetch(`${API_URL}/api/referrals/stats`, h),
        fetch(`${API_URL}/api/referrals/earnings?limit=50`, h),
        fetch(`${API_URL}/api/referrals/users?limit=50`, h),
      ]);
      if (s.ok) setStats(await s.json());
      if (e.ok) setEarnings((await e.json()).earnings || []);
      if (u.ok) setUsers((await u.json()).users || []);
    } catch (err) {
      console.error("Failed to fetch referral data:", err);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(stats.referral_link);
    toast.success(t("ref.copied"));
  };

  const shareLink = async () => {
    const text = t("ref.shareText", { welcome: fmt(stats.welcome_bonus) });
    if (navigator.share) {
      try { await navigator.share({ title: "TeaChess", text, url: stats.referral_link }); return; } catch (_) {}
    }
    copyLink();
  };

  const transfer = async () => {
    setTransferring(true);
    try {
      const res = await fetch(`${API_URL}/api/referrals/withdraw`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success(t("ref.transferDone", { amount: fmt(data.amount) }));
      await Promise.all([fetchData(), fetchUser()]);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTransferring(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const pct = stats.percent;
  const canTransfer = stats.referral_balance >= stats.min_payout;
  const steps = [
    { icon: Link2, title: t("ref.step1Title"), text: t("ref.step1Text") },
    { icon: UserPlus, title: t("ref.step2Title"), text: t("ref.step2Text", { welcome: fmt(stats.welcome_bonus), min: fmt(stats.first_deposit_min), bonus: fmt(stats.first_deposit_bonus) }) },
    { icon: Percent, title: t("ref.step3Title"), text: t("ref.step3Text", { percent: pct, example: fmt(1000 * pct / 100) }) },
    { icon: ArrowDownToLine, title: t("ref.step4Title"), text: t("ref.step4Text", { min: fmt(stats.min_payout) }) },
  ];
  const tiers = [
    { key: "basic", badge: "🥉", pct: 3, range: t("ref.tier1"), cls: "bg-orange-500/20 border border-orange-500/50" },
    { key: "gold", badge: "🥇", pct: 7, range: t("ref.tier2"), cls: "bg-yellow-500/20 border border-yellow-500/50" },
    { key: "diamond", badge: "💎", pct: 10, range: t("ref.tier3"), cls: "bg-cyan-500/20 border border-cyan-500/50" },
  ];

  return (
    <div className="space-y-4 sm:space-y-6 px-1 sm:px-0 pb-8" data-testid="referrals-page">
      <Button variant="ghost" onClick={() => navigate("/lobby")} className="h-8 sm:h-10 text-sm" size="sm">
        <ArrowLeft className="h-4 w-4 mr-2" />{t("common.back")}
      </Button>

      <div className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight flex items-center gap-2">
          <Gift className="h-7 w-7 text-primary" />{t("ref.title")}
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground" data-testid="ref-subtitle">
          {t("ref.subtitle", { percent: pct })}
        </p>
      </div>

      {/* Link */}
      <Card className="glass border-primary/30 glow-gold">
        <CardContent className="pt-4 sm:pt-6 space-y-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("ref.yourLink")}</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-background/50 rounded-lg p-3 font-mono text-xs sm:text-sm truncate" data-testid="ref-link">
              {stats.referral_link}
            </div>
            <Button size="icon" onClick={copyLink} className="shrink-0" data-testid="copy-link-btn"><Copy className="h-4 w-4" /></Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("ref.yourCode")}: <span className="font-mono font-bold text-foreground" data-testid="ref-code">{stats.referral_code}</span>
          </p>
          <div className="flex gap-2">
            <Button className="flex-1 h-10 sm:h-11" onClick={shareLink} data-testid="share-btn">
              <Share2 className="h-4 w-4 mr-2" />{t("common.share")}
            </Button>
            <Button variant="outline" className="flex-1 h-10 sm:h-11" onClick={() => window.open(stats.telegram_link, "_blank")} data-testid="telegram-btn">
              <Send className="h-4 w-4 mr-2" />Telegram
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* How it works */}
      <Card className="glass" data-testid="ref-how-it-works">
        <CardHeader className="pb-2"><CardTitle className="text-base sm:text-lg">{t("ref.howTitle")}</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-3">
          {steps.map((s, i) => (
            <div key={i} className="flex gap-3 p-3 rounded-lg bg-muted/30" data-testid={`ref-step-${i + 1}`}>
              <div className="h-9 w-9 shrink-0 rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold text-sm">
                {i + 1}
              </div>
              <div className="space-y-1">
                <p className="font-semibold text-sm flex items-center gap-1.5"><s.icon className="h-4 w-4 text-primary" />{s.title}</p>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{s.text}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        {[
          { icon: Users, color: "text-blue-500", val: stats.total_referrals, label: t("ref.statReferrals") },
          { icon: TrendingUp, color: "text-green-500", val: stats.active_referrals, label: t("ref.statActive") },
          { icon: Wallet, color: "text-yellow-500", val: `${fmt(stats.referral_balance)} P`, label: t("ref.statPending") },
          { icon: Crown, color: "text-purple-500", val: `${fmt(stats.total_earnings)} P`, label: t("ref.statTotal") },
        ].map((c, i) => (
          <Card key={i} className="glass">
            <CardContent className="pt-4 text-center">
              <c.icon className={`h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-1 ${c.color}`} />
              <p className="text-xl sm:text-2xl font-bold">{c.val}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">{c.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Transfer */}
      <Card className={`glass ${canTransfer ? "border-green-500/40" : ""}`} data-testid="ref-transfer-card">
        <CardContent className="pt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="font-semibold">{t("ref.statPending")}: <span className="text-green-500">{fmt(stats.referral_balance)} P</span></p>
            <p className="text-xs text-muted-foreground">{t("ref.transferHint", { min: fmt(stats.min_payout), amount: fmt(stats.referral_balance) })}</p>
          </div>
          <Button onClick={transfer} disabled={!canTransfer || transferring} data-testid="ref-transfer-btn">
            {transferring ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowDownToLine className="h-4 w-4 mr-2" />}
            {t("ref.transfer")}
          </Button>
        </CardContent>
      </Card>

      {/* Levels */}
      <Card className="glass" data-testid="ref-levels">
        <CardHeader className="pb-2">
          <CardTitle className="text-base sm:text-lg">{t("ref.levelsTitle")}</CardTitle>
          <p className="text-xs text-muted-foreground">{t("ref.levelHint")}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{stats.level_badge}</span>
              <div>
                <p className="font-bold" data-testid="ref-current-level">{t(`ref.level${stats.referral_level.charAt(0).toUpperCase()}${stats.referral_level.slice(1)}`)} — {pct}%</p>
                <p className="text-xs text-muted-foreground">{t("ref.currentLevel")}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold">{stats.total_referrals}{stats.next_level_at ? ` / ${fmt(stats.next_level_at)}` : ""}</p>
              <p className="text-xs text-muted-foreground">
                {stats.next_level_at ? `${fmt(stats.next_level_at - stats.total_referrals)} ${t("ref.toNext")}` : t("ref.maxLevel")}
              </p>
            </div>
          </div>
          {stats.next_level_at && (
            <div className="w-full bg-muted rounded-full h-2">
              <div className="bg-gradient-to-r from-yellow-500 to-orange-500 h-2 rounded-full transition-all" style={{ width: `${Math.max(2, Math.min(stats.progress_to_next, 100))}%` }} />
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 text-center text-xs pt-1">
            {tiers.map((tier) => (
              <div key={tier.key} className={`p-2 rounded-lg ${stats.referral_level === tier.key ? tier.cls : "bg-muted/50"}`}>
                <p className="font-bold">{tier.badge} {tier.pct}%</p>
                <p className="text-muted-foreground">{tier.range}</p>
              </div>
            ))}
          </div>
          {stats.referral_level === "individual" && (
            <p className="text-xs text-purple-400">⭐ {t("ref.individual")}: {pct}%</p>
          )}
        </CardContent>
      </Card>

      {/* History */}
      <Tabs defaultValue="earnings" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="earnings" className="text-xs sm:text-sm" data-testid="ref-tab-earnings">{t("ref.tabEarnings")}</TabsTrigger>
          <TabsTrigger value="users" className="text-xs sm:text-sm" data-testid="ref-tab-users">{t("ref.tabUsers")}</TabsTrigger>
        </TabsList>
        <TabsContent value="earnings" className="mt-4">
          <Card className="glass">
            <CardHeader className="pb-2"><CardTitle className="text-base sm:text-lg">{t("ref.earningsTitle")}</CardTitle></CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                {earnings.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">{t("ref.noEarnings")}</p>
                ) : earnings.map((e) => (
                  <div key={e.id} className="flex items-center justify-between p-3 mb-2 rounded-lg bg-muted/30">
                    <div>
                      <p className="font-semibold text-sm">{e.referred_username}</p>
                      <p className="text-xs text-muted-foreground">{t("ref.deposited")} {fmt(e.deposit_amount)} P · {e.percent}% · {new Date(e.created_at).toLocaleDateString(locale)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-500">+{fmt(e.amount)} P</p>
                      <p className={`text-xs ${e.status === "paid" ? "text-green-500" : "text-yellow-500"}`}>{e.status === "paid" ? t("ref.paid") : t("ref.pending")}</p>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="users" className="mt-4">
          <Card className="glass">
            <CardHeader className="pb-2"><CardTitle className="text-base sm:text-lg">{t("ref.usersTitle")}</CardTitle></CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                {users.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">{t("ref.noUsers")}</p>
                ) : users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between p-3 mb-2 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center ${u.is_active ? "bg-green-500/20" : "bg-muted"}`}>
                        <Users className={`h-4 w-4 ${u.is_active ? "text-green-500" : "text-muted-foreground"}`} />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{u.username}</p>
                        <p className="text-xs text-muted-foreground">{t("ref.deposits")}: {fmt(u.total_deposits)} P</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-500">+{fmt(u.total_earned)} P</p>
                      <p className={`text-xs ${u.is_active ? "text-green-500" : "text-muted-foreground"}`}>{u.is_active ? t("ref.active") : t("ref.inactive")}</p>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* FAQ */}
      <Card className="glass" data-testid="ref-faq">
        <CardHeader className="pb-2"><CardTitle className="text-base sm:text-lg">{t("ref.faqTitle")}</CardTitle></CardHeader>
        <CardContent>
          <Accordion type="single" collapsible>
            {[1, 2, 3, 4].map((n) => (
              <AccordionItem key={n} value={`q${n}`}>
                <AccordionTrigger className="text-sm text-left">{t(`ref.faq${n}q`)}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">{t(`ref.faq${n}a`)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
